import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { Outlet, useParams, useLocation, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { usePageTitle } from "@/lib/pageTitle";
import { useI18n } from "@/i18n/I18nContext";

const ExhibitionContext = createContext(null);
export const useExhibition = () => useContext(ExhibitionContext);

const readVisitor = () => { try { return JSON.parse(localStorage.getItem("bg_visitor") || "null"); } catch { return null; } };

export default function ExhibitionShell() {
  const { slug } = useParams();
  const location = useLocation();
  const { lang, setLang, openLanguageSheet, t, langName, setAudioAvailability, prefix } = useI18n();
  const [exhibition, setExhibition] = useState(null);
  const [error, setError] = useState(null);
  const [visitorToken, setVisitorToken] = useState(() => localStorage.getItem("bg_visitor_token"));
  const [visitor, setVisitor] = useState(readVisitor);
  const [visitorChecked, setVisitorChecked] = useState(false);

  const reload = useCallback(() => api.get(`/public/exhibitions/${slug}`).then((r) => setExhibition(r.data)).catch(() => setError("Exhibition not found")), [slug]);
  useEffect(() => { reload(); }, [reload]);
  usePageTitle(exhibition ? `${exhibition.name} – Guide` : null);

  useEffect(() => {
    if (!exhibition) return;
    const map = {};
    exhibition.languages.forEach((l) => { map[l.code] = exhibition.artworks.some((a) => a.content?.[l.code]?.audio_url); });
    setAudioAvailability(map);
    return () => setAudioAvailability(null);
  }, [exhibition, setAudioAvailability]);

  useEffect(() => {
    if (!visitorToken || visitorToken.startsWith("skip-")) { setVisitorChecked(true); return; }
    api.get("/public/visitor", { params: { token: visitorToken } })
      .then((r) => { setVisitor(r.data); localStorage.setItem("bg_visitor", JSON.stringify(r.data)); setVisitorChecked(true); })
      .catch(() => { localStorage.removeItem("bg_visitor_token"); localStorage.removeItem("bg_visitor"); setVisitorToken(null); setVisitor(null); setVisitorChecked(true); });
  }, [visitorToken]);

  const registerVisitor = useCallback((data) => {
    localStorage.setItem("bg_visitor_token", data.token);
    const info = { first_name: data.first_name, last_name: data.last_name, gift: data.gift, code: data.code };
    localStorage.setItem("bg_visitor", JSON.stringify(info));
    setVisitor(info); setVisitorToken(data.token);
  }, []);

  const skipSignup = useCallback(() => {
    const token = `skip-${crypto.randomUUID()}`;
    localStorage.setItem("bg_visitor_token", token);
    localStorage.removeItem("bg_visitor");
    setVisitor(null); setVisitorToken(token);
    api.post("/public/skip", {}).catch(() => {});
  }, []);

  const hasGift = !!(visitor && visitor.gift && visitor.gift !== "none");
  const value = useMemo(() => ({ slug, exhibition, lang, setLang, t, langName, prefix, openLanguageSheet, visitorToken, visitor, hasGift, registerVisitor, skipSignup, reload }),
    [slug, exhibition, lang, setLang, t, langName, prefix, openLanguageSheet, visitorToken, visitor, hasGift, registerVisitor, skipSignup, reload]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-[var(--muted)]" data-testid="exhibition-error">{error}</div>;
  if (!exhibition || !visitorChecked) return <div className="min-h-screen flex items-center justify-center text-[var(--muted)] text-xs tracking-[0.3em] uppercase" data-testid="exhibition-loading">Loading…</div>;

  const onSignup = location.pathname.endsWith("/signup");
  if (!visitorToken && !onSignup) {
    return <Navigate to={`${prefix}/e/${slug}/signup?next=${encodeURIComponent(location.pathname)}`} replace />;
  }

  return (
    <ExhibitionContext.Provider value={value}>
      <div className="min-h-screen" style={{ "--accent": exhibition.accent_color || "#E31839" }}>
        <Outlet />
      </div>
    </ExhibitionContext.Provider>
  );
}
